import { useEffect, useState, useCallback } from 'react';
import { useAuth } from 'react-oidc-context';
import { useApiFetch } from '../api';
import { VirtualMachine } from '../types/models/VirtualMachine';
import { ApiResponseVmShow } from '../types/api/ApiResponseVirtualMachine';

interface UseVirtualMachineReturn {
  vm: VirtualMachine | null;
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export const useVirtualMachine = (vmId: string | number): UseVirtualMachineReturn => {
  const [vm, setVm] = useState<VirtualMachine | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error | null>(null);
  const auth = useAuth();
  const apiFetch = useApiFetch();

  const fetchVm = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const response = await apiFetch(`/datacenters/vms/${vmId}`);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = (await response.json()) as ApiResponseVmShow;
      if (data.status === 'success' && data.data?.vm) {
        setVm(data.data.vm);
      } else {
        throw new Error('Virtual machine not found');
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to load virtual machine'));
    } finally {
      setLoading(false);
    }
  }, [vmId, apiFetch]);

  useEffect(() => {
    if (auth.isAuthenticated) {
      fetchVm();
    }
  }, [auth.isAuthenticated, fetchVm]);

  return { vm, loading, error, refetch: fetchVm };
};
